import {
  MATERIAL_BATCH_POWDER_QUALITY_STATUS_MAP,
  MATERIAL_BATCH_POWDER_QUALITY_STATUSES,
  MATERIAL_BATCH_WARNING_POWDER_QUALITY_STATUSES,
} from 'src/utils/constants';
import { pluralWord } from 'src/utils/stringUtils';
import { hasDifferentMaterialTypeSets } from 'src/utils/validation';

export const BLEND_ATTRIBUTE = {
  MATERIAL_TYPE: 'materialType',
  MATERIAL_LOT: 'materialLot',
  USAGE_CYCLES: 'usageCycles',
  POWDER_QUALITY: 'powderQuality',
};

export const BLEND_ATTRIBUTE_LABELS = {
  [BLEND_ATTRIBUTE.MATERIAL_TYPE]: 'Material Type',
  [BLEND_ATTRIBUTE.MATERIAL_LOT]: 'Material Lot',
  [BLEND_ATTRIBUTE.USAGE_CYCLES]: 'Usage Cycles',
  [BLEND_ATTRIBUTE.POWDER_QUALITY]: 'Powder Quality',
};

// nautilus constants.PowderQualityStatus.get_status_ranking() - higher is worse.
// `done` is deliberately absent there too, so it stays unrankable here.
const POWDER_QUALITY_RANK = {
  [MATERIAL_BATCH_POWDER_QUALITY_STATUSES.NEW]: 1,
  [MATERIAL_BATCH_POWDER_QUALITY_STATUSES.SIEVED]: 2,
  [MATERIAL_BATCH_POWDER_QUALITY_STATUSES.UNSIEVED]: 3,
  [MATERIAL_BATCH_POWDER_QUALITY_STATUSES.UNKNOWN]: 4,
};

export const getPowderQualityLabel = status =>
  MATERIAL_BATCH_POWDER_QUALITY_STATUS_MAP[status] || status || '';

/** Compact one-line value for an attribute, used by summaries and step badges. */
export const getAttributeSummary = attribute => {
  switch (attribute?.key) {
    case BLEND_ATTRIBUTE.MATERIAL_TYPE: {
      const names = [...new Set((attribute.source || []).map(material => material.name))];
      return names.length > 1 ? `${names.length} types` : names[0] || '';
    }
    case BLEND_ATTRIBUTE.MATERIAL_LOT: {
      const count = (attribute.source || []).length;
      return `${count} ${pluralWord('lot', count)}`;
    }
    case BLEND_ATTRIBUTE.USAGE_CYCLES:
      return `${attribute.source ?? 0} ${pluralWord('cycle', attribute.source ?? 0)}`;
    case BLEND_ATTRIBUTE.POWDER_QUALITY:
      return getPowderQualityLabel(attribute.source);
    default:
      return '';
  }
};

const getMaterials = batch => batch?.materials || [];
const getMaterialLotUris = batch => batch?.material_lots || [];
const getUsageCycles = batch => batch?.usage_cycles ?? 0;

export const getBlendedMaterialLots = (sourceBatch, destinationBatch) => [
  ...new Set([...getMaterialLotUris(sourceBatch), ...getMaterialLotUris(destinationBatch)]),
];

export const getBlendedMaterials = (sourceBatch, destinationBatch) => {
  const byUri = new Map();
  [...getMaterials(sourceBatch), ...getMaterials(destinationBatch)].forEach(material => {
    if (!byUri.has(material.uri)) {
      byUri.set(material.uri, material);
    }
  });
  return [...byUri.values()];
};

export const getBlendedUsageCycles = (sourceBatch, destinationBatch) =>
  Math.max(getUsageCycles(sourceBatch), getUsageCycles(destinationBatch));

export const getBlendedPowderQuality = (sourceBatch, destinationBatch) => {
  const sourceStatus = sourceBatch?.powder_quality;
  const destinationStatus = destinationBatch?.powder_quality;
  const sourceRank = POWDER_QUALITY_RANK[sourceStatus];
  const destinationRank = POWDER_QUALITY_RANK[destinationStatus];

  if (!sourceRank || !destinationRank) {
    return null;
  }

  return sourceRank >= destinationRank ? sourceStatus : destinationStatus;
};

const hasDifferentMaterialLots = (sourceBatch, destinationBatch) => {
  const sourceLots = new Set(getMaterialLotUris(sourceBatch));
  const destinationLots = new Set(getMaterialLotUris(destinationBatch));

  if (sourceLots.size !== destinationLots.size) {
    return true;
  }

  return [...sourceLots].some(lotUri => !destinationLots.has(lotUri));
};

const buildMaterialTypeAttribute = (sourceBatch, destinationBatch) => {
  const blendedMaterials = getBlendedMaterials(sourceBatch, destinationBatch);
  const destinationTypes = new Set(getMaterials(destinationBatch).map(material => material.uri));
  const addsTypes = blendedMaterials.length !== destinationTypes.size;

  return {
    key: BLEND_ATTRIBUTE.MATERIAL_TYPE,
    label: BLEND_ATTRIBUTE_LABELS[BLEND_ATTRIBUTE.MATERIAL_TYPE],
    differs: hasDifferentMaterialTypeSets(sourceBatch, destinationBatch),
    source: getMaterials(sourceBatch),
    destination: getMaterials(destinationBatch),
    becomes: {
      materials: blendedMaterials,
      count: blendedMaterials.length,
      reason: addsTypes ? 'the types from both sides, combined' : 'no new type added',
      changesDestination: addsTypes,
    },
  };
};

const buildMaterialLotAttribute = (sourceBatch, destinationBatch) => {
  const blendedLots = getBlendedMaterialLots(sourceBatch, destinationBatch);
  const addsLots = blendedLots.length !== getMaterialLotUris(destinationBatch).length;

  return {
    key: BLEND_ATTRIBUTE.MATERIAL_LOT,
    label: BLEND_ATTRIBUTE_LABELS[BLEND_ATTRIBUTE.MATERIAL_LOT],
    differs: hasDifferentMaterialLots(sourceBatch, destinationBatch),
    source: getMaterialLotUris(sourceBatch),
    destination: getMaterialLotUris(destinationBatch),
    becomes: {
      lotUris: blendedLots,
      count: blendedLots.length,
      reason: addsLots ? 'the lots from both sides, combined' : 'no new lot added',
      changesDestination: addsLots,
    },
  };
};

const buildUsageCyclesAttribute = (sourceBatch, destinationBatch) => {
  const blendedCycles = getBlendedUsageCycles(sourceBatch, destinationBatch);

  return {
    key: BLEND_ATTRIBUTE.USAGE_CYCLES,
    label: BLEND_ATTRIBUTE_LABELS[BLEND_ATTRIBUTE.USAGE_CYCLES],
    differs: getUsageCycles(sourceBatch) !== getUsageCycles(destinationBatch),
    source: getUsageCycles(sourceBatch),
    destination: getUsageCycles(destinationBatch),
    becomes: {
      value: blendedCycles,
      reason: 'the higher of the two, not the total',
      changesDestination: blendedCycles !== getUsageCycles(destinationBatch),
    },
  };
};

const buildPowderQualityAttribute = (sourceBatch, destinationBatch) => {
  const blendedQuality = getBlendedPowderQuality(sourceBatch, destinationBatch);

  return {
    key: BLEND_ATTRIBUTE.POWDER_QUALITY,
    label: BLEND_ATTRIBUTE_LABELS[BLEND_ATTRIBUTE.POWDER_QUALITY],
    differs: sourceBatch?.powder_quality !== destinationBatch?.powder_quality,
    source: sourceBatch?.powder_quality,
    destination: destinationBatch?.powder_quality,
    becomes: blendedQuality && {
      status: blendedQuality,
      reason: 'the lower quality of the two',
      changesDestination: blendedQuality !== destinationBatch?.powder_quality,
      // Unsieved and Unknown are caution states in their own right. A destination
      // that was already Unsieved does not "change", but the operator still ends up
      // with unsieved powder in the printer - that must not read as a green success.
      isCaution: MATERIAL_BATCH_WARNING_POWDER_QUALITY_STATUSES.includes(blendedQuality),
    },
  };
};

const fingerprintValue = value => {
  if (Array.isArray(value)) {
    return value
      .map(item => (typeof item === 'string' ? item : (item?.uri ?? '')))
      .sort()
      .join('+');
  }
  return `${value ?? ''}`;
};

/**
 * Identity of one comparison: the two resources plus the exact values being
 * compared. Any change to either batch yields a different key, so a stored
 * acknowledgement can never carry over to something that is no longer true.
 */
export const buildBlendCheckKey = (mode, sourceBatch, destinationBatch, attributes) => {
  if (!sourceBatch || !attributes?.length) {
    return null;
  }

  const values = attributes
    .map(
      attribute =>
        `${attribute.key}=${fingerprintValue(attribute.source)}>${fingerprintValue(attribute.destination)}`
    )
    .join('|');

  return `${mode}::${sourceBatch.uri}::${destinationBatch?.uri ?? 'none'}::${values}`;
};

/**
 * Compares the two batches an action is about to blend.
 * Returns one entry per attribute, in the order the operator reviews them.
 */
export const compareBlendAttributes = (sourceBatch, destinationBatch) => {
  if (!sourceBatch || !destinationBatch) {
    return [];
  }

  return [
    buildMaterialTypeAttribute(sourceBatch, destinationBatch),
    buildMaterialLotAttribute(sourceBatch, destinationBatch),
    buildUsageCyclesAttribute(sourceBatch, destinationBatch),
    buildPowderQualityAttribute(sourceBatch, destinationBatch),
  ];
};

/**
 * What a single batch contains. All four attributes are returned
 * so the summary can show the whole picture; only types and lots can ever be
 * mixed, since usage cycles and powder quality are single-valued on a batch.
 */
export const getBlendedBatchAttributes = batch => {
  if (!batch) {
    return [];
  }

  const materials = getMaterials(batch);
  const lotUris = getMaterialLotUris(batch);
  const uniqueMaterialUris = new Set(materials.map(material => material.uri));

  const attribute = (key, differs, source) => ({
    key,
    label: BLEND_ATTRIBUTE_LABELS[key],
    differs,
    source,
    destination: null,
    becomes: null,
  });

  return [
    attribute(BLEND_ATTRIBUTE.MATERIAL_TYPE, uniqueMaterialUris.size > 1, materials),
    attribute(BLEND_ATTRIBUTE.MATERIAL_LOT, lotUris.length > 1, lotUris),
    attribute(BLEND_ATTRIBUTE.USAGE_CYCLES, false, getUsageCycles(batch)),
    attribute(BLEND_ATTRIBUTE.POWDER_QUALITY, false, batch.powder_quality),
  ];
};

export const isBlendedBatch = batch =>
  getBlendedBatchAttributes(batch).some(attribute => attribute.differs);
